import { useState, useEffect } from "react";
import { useToast } from "../../../context/ToastContext";
import "../../../assets/css/loanSettings.css";
import BaseModal from "../../../components/BaseModal";

const API_BASE = `${process.env.REACT_APP_API_BASE}/loans/loan_settings.php`;

const HRM_SETTINGS_API = `${process.env.REACT_APP_API_BASE}/hrm/hrm_settings.php`;

export default function LoanSettings() {
  const { showToast } = useToast();

  const [confirmSaveOpen, setConfirmSaveOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadingSettings, setLoadingSettings] = useState(true);

  const [approvalEnabled, setApprovalEnabled] = useState(true);
  const [approvalLevels, setApprovalLevels] = useState("2");
  const [requireRejectComment, setRequireRejectComment] = useState(true);
  const [allowAmountEdit, setAllowAmountEdit] = useState(true);

  const [loanProducts, setLoanProducts] = useState<any[]>([]);
  const [levelOneApprovers, setLevelOneApprovers] = useState<string[]>([]);
  const [levelTwoApprovers, setLevelTwoApprovers] = useState<string[]>([]);

  const [defaultApproverRole, setDefaultApproverRole] = useState("");

  const [hrmDesignations, setHrmDesignations] = useState<string[]>([]);

  const [productForm, setProductForm] = useState({
  name: "",
  minAmount: "",
  maxAmount: "",
  minTenure: "",
  maxTenure: "",
  rate: "",
  moratorium: "",
upfrontFeeRate: "0",
liquidationFeeRate: "5",
interestChargeMonths: "1",
chargeTotalPrincipal: true,
});

const [editingProductIndex, setEditingProductIndex] = useState<number | null>(null);

  const parseApiResponse = async (res: Response, type: "LOAD" | "SAVE") => {
    const text = await res.text();

    let result: any = {};
    try {
      result = text ? JSON.parse(text) : {};
    } catch (e) {
      console.error(`RAW ${type} RESPONSE:`, text);
      throw new Error(text || "Backend did not return valid JSON.");
    }

    return result;
  };

  const cleanNumber = (value: string) => {
    if (value === "") return "";
    return String(Math.max(0, Number(value)));
  };

  const blockInvalidNumberKeys = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "-" || e.key.toLowerCase() === "e" || e.key === "+") {
      e.preventDefault();
    }
  };


  const loadhrmDesignations = async () => {
  try {
    const res = await fetch(HRM_SETTINGS_API, {
      method: "GET",
      credentials: "include",
    });

    const result = await parseApiResponse(res, "LOAD");

    if (res.ok && result.success) {
     setHrmDesignations(result.data?.designations || []);
    }
  } catch (error) {
    console.error("Failed to load HRM roles:", error);
  }
};

  const loadLoanSettings = async () => {
    try {
      setLoadingSettings(true);

      const res = await fetch(API_BASE, {
        method: "GET",
        credentials: "include",
      });

      const result = await parseApiResponse(res, "LOAD");

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to load settings");
      }

      const { settings, approvers = [], products = [] } = result;

      if (settings) {
        setDefaultApproverRole(settings.default_approver_role || "");
        setApprovalEnabled(!!Number(settings.approval_enabled));
        setApprovalLevels(String(settings.approval_levels || "2"));
        setRequireRejectComment(!!Number(settings.require_reject_comment));
        setAllowAmountEdit(!!Number(settings.allow_amount_edit));
      }

      const level1 = approvers
        .filter((a: any) => Number(a.level) === 1)
        .map((a: any) => a.approver_name);

      const level2 = approvers
        .filter((a: any) => Number(a.level) === 2)
        .map((a: any) => a.approver_name);

      setLevelOneApprovers(level1);
      setLevelTwoApprovers(level2);

    const formattedProducts = products.map((p: any) => ({
  name: p.product_name,
  minAmount: p.min_amount,
  maxAmount: p.max_amount,
  minTenure: p.min_tenure,
  maxTenure: p.max_tenure,
  rate: p.interest_rate,
  moratorium: p.moratorium,
  upfrontFeeRate: String(p.upfront_fee_rate ?? "0"),
liquidationFeeRate: String(p.liquidation_fee_rate ?? "5"),
interestChargeMonths: String(p.interest_charge_months ?? "1"),
  chargeTotalPrincipal: Number(p.charge_total_principal ?? 1) === 1,
}));

      setLoanProducts(formattedProducts);
    } catch (error: any) {
      showToast("error", "Load Failed", error.message);
    } finally {
      setLoadingSettings(false);
    }
  };

 useEffect(() => {
  loadLoanSettings();
  loadhrmDesignations();
}, []);

const saveProductToList = () => {
  if (
    !productForm.name ||
    !productForm.minAmount ||
    !productForm.maxAmount ||
    !productForm.minTenure ||
    !productForm.maxTenure ||
    !productForm.rate
  ) {
    showToast("error", "Required Fields", "Please fill all required fields.");
    return;
  }

  if (Number(productForm.minAmount) > Number(productForm.maxAmount)) {
    showToast("error", "Invalid Amount", "Minimum amount cannot be greater than maximum.");
    return;
  }

  if (Number(productForm.minTenure) > Number(productForm.maxTenure)) {
    showToast("error", "Invalid Tenure", "Lower tenure cannot be greater than highest tenure.");
    return;
  }

  if (Number(productForm.minAmount) <= 0) {
    showToast("error", "Invalid Amount", "Minimum amount must be greater than zero.");
    return;
  }

  if (Number(productForm.maxAmount) <= 0) {
    showToast("error", "Invalid Amount", "Maximum amount must be greater than zero.");
    return;
  }

  if (Number(productForm.minTenure) <= 0) {
    showToast("error", "Invalid Tenure", "Tenure must be greater than zero.");
    return;
  }

  if (Number(productForm.maxTenure) <= 0) {
    showToast("error", "Invalid Tenure", "Highest tenure must be greater than zero.");
    return;
  }

  if (Number(productForm.rate) <= 0) {
    showToast("error", "Invalid Rate", "Interest rate must be greater than zero.");
    return;
  }

  if (Number(productForm.upfrontFeeRate) < 0) {
  showToast("error", "Invalid Fee", "Upfront fee cannot be negative.");
  return;
}


  if (editingProductIndex !== null) {
    setLoanProducts((prev) =>
      prev.map((item, index) =>
        index === editingProductIndex ? { ...productForm } : item
      )
    );

    showToast("success", "Product Updated", "Loan product updated successfully.");
  } else {
    setLoanProducts((prev) => [...prev, { ...productForm }]);
    showToast("success", "Product Added", "Loan product added successfully.");
  }

  setEditingProductIndex(null);

  setProductForm({
    name: "",
    minAmount: "",
    maxAmount: "",
    minTenure: "",
    maxTenure: "",
    rate: "",
    moratorium: "",
   liquidationFeeRate: "5",
interestChargeMonths: "1",
chargeTotalPrincipal: true,
upfrontFeeRate: "0",
  });
};

  const saveLoanSettings = async () => {
    try {
      setSaving(true);

      const payload = {
        approval_enabled: approvalEnabled,
        approval_levels: Number(approvalLevels),
        require_reject_comment: requireRejectComment,
        allow_amount_edit: allowAmountEdit,
          default_approver_role: defaultApproverRole,
        approvers: [
          ...levelOneApprovers.map((name) => ({
            level: 1,
            approver_name: name,
          })),
          ...levelTwoApprovers.map((name) => ({
            level: 2,
            approver_name: name,
          })),
        ],
        products: loanProducts.map((product) => ({
          product_name: product.name,
          min_amount: Number(product.minAmount),
          max_amount: Number(product.maxAmount),
          min_tenure: Number(product.minTenure),
          max_tenure: Number(product.maxTenure),
          interest_rate: Number(product.rate),
          moratorium: Number(product.moratorium || 0),
upfront_fee_rate: Number(product.upfrontFeeRate || 0),
          liquidation_fee_rate: Number(product.liquidationFeeRate || 0),
interest_charge_months: Number(product.interestChargeMonths || 1),
charge_total_principal: product.chargeTotalPrincipal ? 1 : 0,

        })),
      };

      const res = await fetch(API_BASE, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const result = await parseApiResponse(res, "SAVE");

      if (!res.ok || !result.success) {
        throw new Error(result.message || "Failed to save settings");
      }

      setConfirmSaveOpen(false);
      showToast("success", "Settings Saved", "Loan settings saved successfully.");
      await loadLoanSettings();
    } catch (error: any) {
      showToast("error", "Save Failed", error.message || "Something went wrong.");
    } finally {
      setSaving(false);
    }
  };

  if (loadingSettings) {
    return (
      <div className="loan-settings-page">
        <div className="loan-settings-card">
          <h4>Loading settings...</h4>
          <p>Please wait while we fetch loan settings.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="loan-settings-page">
      <div className="loan-settings-header">
        <div>
          <span>System Settings</span>
          <h2>Loan Approval Settings</h2>
          <p>Configure how loan applications are reviewed and approved.</p>
        </div>
      </div>

      <div className={approvalEnabled ? "loan-settings-grid" : "loan-settings-grid single"}>
        <div className="loan-settings-card">
          <h4>Approval Configuration</h4>
          <p>Control the approval workflow for loan applications.</p>

          <div className="settings-row">
            <div>
              <strong>Enable Loan Approval</strong>
              <small>Require approval before loans can be disbursed.</small>
            </div>

            <label className="switch">
              <input
                type="checkbox"
                checked={approvalEnabled}
                onChange={(e) => setApprovalEnabled(e.target.checked)}
              />
              <span />
            </label>
          </div>

          {approvalEnabled ? (
            <>
              <div className="settings-field">
                <label>Approval Levels</label>
                <select value={approvalLevels} onChange={(e) => setApprovalLevels(e.target.value)}>
                  <option value="1">One Level Approval</option>
                  <option value="2">Two Level Approval</option>
                </select>
              </div>

              <div className="settings-row">
                <div>
                  <strong>Require Comment for Reject / Return</strong>
                  <small>Reviewer must enter a reason before submitting.</small>
                </div>

                <label className="switch">
                  <input
                    type="checkbox"
                    checked={requireRejectComment}
                    onChange={(e) => setRequireRejectComment(e.target.checked)}
                  />
                  <span />
                </label>
              </div>

              <div className="settings-row">
                <div>
                  <strong>Allow Approved Amount Editing</strong>
                  <small>Approver can approve a different amount.</small>
                </div>

                <label className="switch">
                  <input
                    type="checkbox"
                    checked={allowAmountEdit}
                    onChange={(e) => setAllowAmountEdit(e.target.checked)}
                  />
                  <span />
                </label>
              </div>
            </>
       ) : (
<>
  <div className="approval-disabled-box">
  <i className="fa fa-info-circle" />
  <div>
    <strong>Approval workflow is disabled</strong>
    <p>
      Loan applications will not pass through approval levels when this is turned off.
    </p>
  </div>
</div>

<div className="settings-field">
  <label>Default Approver Role</label>
<select
  value={defaultApproverRole}
  onChange={(e) => setDefaultApproverRole(e.target.value)}
>
  {/* ✅ ADD THIS LINE */}
  <option value="">Select default approver role</option>

  {hrmDesignations.map((role) => (
    <option key={role} value={role}>
      {role}
    </option>
  ))}
</select>
</div>
</>
          )}
        </div>

        {approvalEnabled && (
          <div className="loan-settings-card">
            <h4>Approval Flow Preview</h4>
            <p>This is how loans will move through approval.</p>

            <div className="approval-flow">
              <FlowStep number="1" title="Loan Submitted" text="Customer application is received." />
              <div className="flow-line" />
             <FlowStep
  number="2"
  title="Level 1 Approval"
  text={
    levelOneApprovers.length > 0
      ? levelOneApprovers.join(" • ")
      : "No approver assigned"
  }
/>

              {approvalLevels === "2" && (
                <>
                  <div className="flow-line" />
                 <FlowStep
  number="3"
  title="Level 2 Approval"
  text={
    levelTwoApprovers.length > 0
      ? levelTwoApprovers.join(" • ")
      : "No approver assigned"
  }
/>
                </>
              )}

              <div className="flow-line" />

              <div className="flow-step success">
                <span>
                  <i className="fa fa-check" />
                </span>
                <div>
                  <strong>Approved</strong>
                  <small>Loan can proceed to disbursement.</small>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {approvalEnabled && (
        <div className="loan-settings-grid">
        <ApproverCard
  title="Level 1 Approvers"
  description="Users who can perform first approval."
  approvers={levelOneApprovers}
  setApprovers={setLevelOneApprovers}
  roles={hrmDesignations}
/>

          {approvalLevels === "2" && (
          <ApproverCard
  title="Level 2 Approvers"
  description="Users who can perform final approval."
  approvers={levelTwoApprovers}
  setApprovers={setLevelTwoApprovers}
  roles={hrmDesignations}
/>
          )}
        </div>
      )}

      <div className="loan-settings-card loan-products-card">
        <h4>Loan Products</h4>
        <p>Configure product limits, tenure, rate and moratorium.</p>

       <div className="loan-product-form">
  <div className="product-field">
    <label>Product Name</label>
    <input
      placeholder="Product Name"
      value={productForm.name}
      onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
    />
  </div>


  

  <div className="product-field">
    <label>Minimum Amount</label>
    <input
      type="number"
      placeholder="Minimum Amount"
      value={productForm.minAmount}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) => setProductForm({ ...productForm, minAmount: cleanNumber(e.target.value) })}
    />
  </div>

  <div className="product-field">
    <label>Maximum Amount</label>
    <input
      type="number"
      placeholder="Maximum Amount"
      value={productForm.maxAmount}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) => setProductForm({ ...productForm, maxAmount: cleanNumber(e.target.value) })}
    />
  </div>

  <div className="product-field">
    <label>Lower Tenure</label>
    <input
      className="small-number-input"
      type="number"
      min="0"
      placeholder="Lower Tenure"
      value={productForm.minTenure}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) => setProductForm({ ...productForm, minTenure: cleanNumber(e.target.value) })}
    />
  </div>

  <div className="product-field">
    <label>Highest Tenure</label>
    <input
      className="small-number-input"
      type="number"
      min="0"
      placeholder="Highest Tenure"
      value={productForm.maxTenure}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) => setProductForm({ ...productForm, maxTenure: cleanNumber(e.target.value) })}
    />
  </div>

  <div className="product-field">
    <label>Interest Rate (%)</label>
    <input
      className="small-number-input"
      type="number"
      min="0"
      placeholder="Rate %"
      value={productForm.rate}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) => setProductForm({ ...productForm, rate: cleanNumber(e.target.value) })}
    />
  </div>

  <div className="product-field">
  <label>Upfront Fee (%)</label>
  <input
    className="small-number-input"
    type="number"
    min="0"
    placeholder="Upfront Fee %"
    value={productForm.upfrontFeeRate}
    onKeyDown={blockInvalidNumberKeys}
    onChange={(e) =>
      setProductForm({
        ...productForm,
        upfrontFeeRate: cleanNumber(e.target.value),
      })
    }
  />
</div>

  <div className="product-field">
    <label>Moratorium Months</label>
    <input
      className="small-number-input"
      type="number"
      min="0"
      placeholder="Moratorium"
      value={productForm.moratorium}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) => setProductForm({ ...productForm, moratorium: cleanNumber(e.target.value) })}
    />
  </div>

  <div className="product-field">
    <label>Liquidation Fee (%)</label>
    <input
      className="small-number-input"
      type="number"
      min="0"
      placeholder="Liquidation Fee %"
      value={productForm.liquidationFeeRate}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) =>
        setProductForm({
          ...productForm,
          liquidationFeeRate: cleanNumber(e.target.value),
        })
      }
    />
  </div>

  <div className="product-field">
    <label>Interest Charge Months</label>
    <input
      className="small-number-input"
      type="number"
      min="0"
      placeholder="Interest Charge Months"
      value={productForm.interestChargeMonths}
      onKeyDown={blockInvalidNumberKeys}
      onChange={(e) =>
        setProductForm({
          ...productForm,
          interestChargeMonths: cleanNumber(e.target.value),
        })
      }
    />
  </div>

  <label className="product-check">
    <input
      type="checkbox"
      checked={productForm.chargeTotalPrincipal}
      onChange={(e) =>
        setProductForm({
          ...productForm,
          chargeTotalPrincipal: e.target.checked,
        })
      }
    />
    <span>Total Principal</span>
  </label>

  <div className="loan-product-actions">
    <button type="button" className="add-product-btn" onClick={saveProductToList}>
      {editingProductIndex !== null ? (
        <>
          <i className="fa fa-save" /> Update Product
        </>
      ) : (
        <>
          <i className="fa fa-plus" /> Add Product
        </>
      )}
    </button>
  </div>
</div>






        <div className="loan-product-table">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Product</th>
                <th>Min Amount</th>
                <th>Max Amount</th>
                <th>Tenure</th>
                <th>Rate</th>
                <th>Upfront Fee</th>
              <th>Moratorium</th>
<th>Liquidation Fee</th>
<th>Interest Months</th>
<th>Total Principal</th>
<th>Action</th>
              </tr>
            </thead>

            <tbody>
              {loanProducts.length === 0 ? (
                <tr>
                 <td colSpan={12} className="empty-table-text">
                    No loan product configured yet.
                  </td>
                </tr>
              ) : (
                loanProducts.map((product, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{product.name}</td>
                    <td>₦{Number(product.minAmount || 0).toLocaleString()}</td>
                    <td>₦{Number(product.maxAmount || 0).toLocaleString()}</td>
                    <td>{product.minTenure} - {product.maxTenure} months</td>
                    <td>{product.rate}%</td>
                    <td>{product.upfrontFeeRate || 0}%</td>
                   <td>{product.moratorium || 0} months</td>
<td>{product.liquidationFeeRate || 0}%</td>
<td>{product.interestChargeMonths || 0} month(s)</td>
<td>{product.chargeTotalPrincipal ? "Yes" : "No"}</td>
<td>
  <div className="product-action-group">
    <button
      type="button"
      className="edit-product-btn"
      onClick={() => {
        setProductForm({
          name: product.name || "",
          minAmount: product.minAmount || "",
          maxAmount: product.maxAmount || "",
          minTenure: product.minTenure || "",
          maxTenure: product.maxTenure || "",
          rate: product.rate || "",
          moratorium: product.moratorium || "",
          upfrontFeeRate: product.upfrontFeeRate || "0",
          liquidationFeeRate: product.liquidationFeeRate || "5",
          interestChargeMonths: product.interestChargeMonths || "1",
          chargeTotalPrincipal: product.chargeTotalPrincipal ?? true,
        });

        setEditingProductIndex(index);
        
      }}
    >
      Edit
    </button>

    <button
      type="button"
      className="remove-product-btn"
      onClick={() => {
        setLoanProducts((prev) => prev.filter((_, i) => i !== index));
        showToast("info", "Removed", "Product removed. Click Save Settings to apply changes.");
      }}
    >
      Remove
    </button>
  </div>
</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="loan-settings-footer">
        <button className="loan-settings-save-btn" onClick={() => setConfirmSaveOpen(true)}>
          <i className="fa fa-save" /> Save Settings
        </button>
      </div>

      <BaseModal
        isOpen={confirmSaveOpen}
        title="Confirm Save"
        onClose={() => setConfirmSaveOpen(false)}
        size="sm"
        showClose={true}
        icon={<i className="fa fa-shield-alt" />}
        footer={
          <>
            <button type="button" className="btn-outline" onClick={() => setConfirmSaveOpen(false)}>
              Cancel
            </button>

            <button
              type="button"
              className="loan-settings-save-btn"
              onClick={saveLoanSettings}
              disabled={saving}
            >
              {saving ? "Saving..." : <><i className="fa fa-check" /> Confirm Save</>}
            </button>
          </>
        }
      >
        <p>Are you sure you want to save these loan approval settings?</p>
      </BaseModal>
    </div>
  );
}

function FlowStep({ number, title, text }: { number: string; title: string; text: string }) {
  return (
    <div className="flow-step">
      <span>{number}</span>
      <div>
        <strong>{title}</strong>
        <small>{text}</small>
      </div>
    </div>
  );
}

function ApproverCard({
  title,
  description,
  approvers,
  setApprovers,
  roles,
}: {
  title: string;
  description: string;
  approvers: string[];
  setApprovers: React.Dispatch<React.SetStateAction<string[]>>;
  roles: string[];
}) {
  const [name, setName] = useState("");
  const { showToast } = useToast();

  const addApprover = () => {
    if (!name.trim()) {
      showToast("error", "Input Required", "Please select an approver role.");
      return;
    }

    if (approvers.includes(name)) {
      showToast("error", "Duplicate Role", "This role already exists.");
      return;
    }

    setApprovers((prev) => [...prev, name]);
    showToast("success", "Added", "Approver added successfully.");
    setName("");
  };

  return (
    <div className="loan-settings-card">
      <h4>{title}</h4>
      <p>{description}</p>

      <div className="approver-list">
        {approvers.map((approver, index) => (
          <div className="approver-item" key={index}>
            <div>
              <strong>{approver}</strong>
              <small>Approval access enabled</small>
            </div>

            <button onClick={() => setApprovers((prev) => prev.filter((_, i) => i !== index))}>
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="add-approver">
        <select value={name} onChange={(e) => setName(e.target.value)} className="approver-select">
          <option value="">Select approver role</option>
         {roles.map((role) => (
  <option key={role} value={role}>
    {role}
  </option>
))}
        </select>

        <button onClick={addApprover} className="add-approver-btn">
          <i className="fa fa-plus" /> Add
        </button>
      </div>
    </div>
  );
}